import { useEffect, useState } from "react";

import { GridLayout } from "@newsela/angelou";
import PropTypes from "prop-types";

import CardFooter from "@/components/CardFooter";
import DeckOfCards from "@/components/DeckOfCards";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import contentTypes from "@/view/constants/contentTypes";
import uiTypes from "@/view/constants/uiTypes";
import findSensitiveContentTag from "@/view/utils/findSensitiveContentTag";
import useFetchRelatedContentCards from "@/view/utils/useFetchRelatedContentCards";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import shuffleRelatedContentCards from "../utils/shuffleRelatedContentCards";
import { gridCss, relatedContentTitle, rootCss } from "./RelatedContent.style";

const propTypes = {
  content: PropTypes.object,
  contentMetadata: PropTypes.arrayOf(PropTypes.object),
  legacyArticleLevel: PropTypes.object,
  userAssignments: PropTypes.arrayOf(PropTypes.object),
  userProperties: PropTypes.object,
};

const RelatedContent = ({
  content,
  contentMetadata,
  legacyArticleLevel,
  userProperties,
}) => {
  const { uiType } = useUserContentUIContext();
  const { isStudent, canViewSearch } = userProperties;
  const contentIsVideo =
    content.content_type === contentTypes.INTERACTIVE_VIDEO;
  const contentIsGenerationGeniusVideo =
    content.content_type === contentTypes.GENERATION_GENIUS_VIDEO;
  const contentId =
    contentIsVideo || contentIsGenerationGeniusVideo
      ? content.id
      : legacyArticleLevel?.header_content_id;
  const [fetchRelatedContentCards, relatedContentCards] =
    useFetchRelatedContentCards({
      contentId: content.id,
      isStudent,
      legacyFormat: true,
    });
  const { isVideoPresentModeActive } = usePresentModeContext();
  // State is required for shuffled cards since relatedContentCards is empty on first render
  const [shuffledCards, setShuffledCards] = useState();
  const liteUserLiteContent = uiType === uiTypes.LITE;
  const hideRelatedContentForFreeStudentUserInTrial =
    liteUserLiteContent && isStudent;

  useEffect(() => {
    fetchRelatedContentCards();
  }, []);

  useEffect(() => {
    setShuffledCards(shuffleRelatedContentCards(relatedContentCards));
  }, [relatedContentCards.length]);

  const isContentSensitive =
    contentMetadata && findSensitiveContentTag(contentMetadata);

  const shouldNotDisplayRelatedContent =
    !contentMetadata ||
    isContentSensitive ||
    !contentId ||
    !shuffledCards?.length ||
    isVideoPresentModeActive ||
    hideRelatedContentForFreeStudentUserInTrial ||
    !canViewSearch;

  if (shouldNotDisplayRelatedContent) {
    return null;
  }

  return (
    <div css={rootCss}>
      <h2 css={relatedContentTitle}>Related content</h2>
      <GridLayout minChildWidth="200px" __cssFor={gridCss}>
        <BookmarkContextProvider>
          {shuffledCards?.map((card) => (
            <DeckOfCards
              {...card}
              key={card.content_id}
              footer={
                <CardFooter
                  contentId={card.content_id}
                  contentType={card.content_type}
                  renderAssignButton={!isStudent}
                  visibilityMarkers={card.visibility_markers}
                />
              }
              isVertical
              trackImpression
            />
          ))}
        </BookmarkContextProvider>
      </GridLayout>
    </div>
  );
};

RelatedContent.propTypes = propTypes;

export default RelatedContent;
